import React from 'react'
import * as yup from 'yup'
import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { CircularProgress, Grid } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { BaseField, StyledButton } from 'shared'
import { useStepper } from 'shared'
import { changeEmail } from 'api/auth'

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Enter a valid email')
    .required('Email is required'),
})

type FormValues = { email: string }

type Props = {
  onEmailSubmitted: (email: string) => void
  closeModal: () => void
}

const EnterEmailStep = ({ onEmailSubmitted, closeModal }: Props) => {
  const { goToNextStep } = useStepper()

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema),
    defaultValues: { email: '' },
  })

  const { isLoading, mutate } = useMutation({
    mutationFn: changeEmail,
    onSuccess: (_data, variables) => {
      onEmailSubmitted(variables.email)
      goToNextStep()
    },
    onError: () => {
      toast.error('Failed to initiate email change. Please try again.')
    },
  })

  const onSubmit = handleSubmit((values) => {
    mutate({ email: values.email })
  })

  return (
    <form onSubmit={onSubmit}>
      <Controller
        control={control}
        name="email"
        render={({ field, fieldState }) => (
          <BaseField
            name=""
            value={field.value}
            label="New email address"
            placeholder="Enter new email"
            type="email"
            error={fieldState.invalid}
            helperText={fieldState.error?.message}
            onChange={(e) => field.onChange(e.target.value)}
          />
        )}
      />
      <Grid
        mt={2}
        gap={2}
        container
        alignItems="center"
        justifyContent="flex-end"
      >
        <StyledButton variant="outlined" onClick={closeModal}>
          Cancel
        </StyledButton>
        <StyledButton
          type="submit"
          variant="contained"
          disabled={!isDirty || !isValid || isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress sx={{ color: 'white' }} size="1em" />
            ) : null
          }
        >
          Send code
        </StyledButton>
      </Grid>
    </form>
  )
}

export default EnterEmailStep
